import { select, put, call, SelectEffect, CallEffect, PutEffect } from 'redux-saga/effects';

import { updateBitmapFonts } from 'js/actionCreators/fontActions';
import {
  setActiveScene,
  setSelectedScenes,
  createTextElement as createTextElementAction,
  updateScribe as updateScribeAction
} from 'js/actionCreators/scribeActions';
import { UpgradeSubscriptionClickedEventTrigger } from 'js/actionCreators/trackingActions';
import { showLeftHandPanel } from 'js/actionCreators/uiActions';
import { upgradeAccount } from 'js/actionCreators/userAccountAction';
import { ACCOUNT_TYPES } from 'js/config/consts';
import { FREE_ELEMENTS_LIMIT } from 'js/config/defaults';
import { AccountType, ExistingScribeModel, LeftHandPanel, ScribeClipboard, UIState } from 'js/types';
import { EditorPasteAction } from 'js/actionCreators/editorActions';

import pasteElementsIntoScribe from '../sagaHelpers/pasteElementsIntoScribe';
import pasteScenesIntoScribe from '../sagaHelpers/pasteScenesIntoScribe';
import { isValidClipboardObject, writeToClipboard } from '../sagaHelpers/writeToClipboard';
import { getScribeById, getUserAccountType, getActiveElements } from '../selectors';

function* userCanAddElements(
  scribeId: number,
  clipboard: ScribeClipboard
): Generator<SelectEffect, boolean, (ExistingScribeModel | undefined) | AccountType> {
  const scribe = (yield select(state => getScribeById(state, scribeId))) as ExistingScribeModel | undefined;
  const elementsInCurrentScribe = scribe?.elements.length || 0;
  const elementsInClipboard = clipboard.data.length;

  const userAccountType = (yield select(getUserAccountType)) as AccountType;

  return elementsInCurrentScribe + elementsInClipboard <= FREE_ELEMENTS_LIMIT || userAccountType !== ACCOUNT_TYPES.FREE;
}

export function* paste({
  scribeId,
  inPlace,
  initialContent
}: EditorPasteAction): Generator<SelectEffect | CallEffect, void, ExistingScribeModel | undefined> {
  if (!initialContent) return;

  const scribe = (yield select(getScribeById, scribeId)) as ExistingScribeModel | undefined;

  if (!scribe) return;

  try {
    const contentInObj = JSON.parse(initialContent);
    if (!isValidClipboardObject(contentInObj)) {
      yield call(pasteText, scribe, initialContent);
      return;
    }
    yield call(pasteSceneOrElement, scribe, contentInObj, inPlace);
  } catch (e) {
    if (e instanceof SyntaxError) {
      yield call(pasteText, scribe, initialContent);
    }
  }
}

function* pasteSceneOrElement(
  scribe: ExistingScribeModel,
  clipboardPayload: ScribeClipboard,
  inPlace: boolean
): Generator<SelectEffect | CallEffect | PutEffect, void, string[] | UIState> {
  if (clipboardPayload.type === 'scene') {
    if (clipboardPayload.metadata.canvasSize !== scribe.canvasSize) return;

    const selectedSceneIds = (yield select(state => state.scribes.selectedSceneIds)) as string[];
    const { leftHandPanel } = (yield select(state => state.ui)) as UIState;
    const { updatedScribe, lastSceneId } = pasteScenesIntoScribe(scribe, clipboardPayload, selectedSceneIds);

    if (leftHandPanel !== LeftHandPanel.SCENES) yield put(showLeftHandPanel(LeftHandPanel.SCENES));
    yield put(updateScribeAction(updatedScribe));
    if (lastSceneId) {
      yield put(setActiveScene(scribe.id, lastSceneId));
    }

    const isContainTextElements = clipboardPayload.data.some(scene => {
      return scene.elementIds.some(elementId => {
        return clipboardPayload.metadata.elements.find(element => element.tempId === elementId)?.type === 'Text';
      });
    });
    if (isContainTextElements) {
      const updatedTextElements = updatedScribe.elements.filter(element => element.type === 'Text');
      yield put(updateBitmapFonts(updatedTextElements));
    }
  }

  if (clipboardPayload.type === 'element') {
    const userCanAddElement = yield call(userCanAddElements, scribe.id, clipboardPayload);
    if (userCanAddElement) {
      const activeElementIds = yield select(getActiveElements);
      const {
        scribe: updatedScribe,
        newElementIds,
        clipboard: updatedClipboardPayload
      } = pasteElementsIntoScribe(
        scribe,
        clipboardPayload,
        activeElementIds,
        inPlace || clipboardPayload.metadata.wasCut
      ) as { scribe: ExistingScribeModel; newElementIds: string[]; clipboard: ScribeClipboard };
      yield put(updateScribeAction(updatedScribe, false, newElementIds));
      yield call(writeToClipboard, updatedClipboardPayload);
      yield put(setSelectedScenes([]));

      const isContainTextElements = clipboardPayload.data.some(element => element.type === 'Text');
      if (isContainTextElements) {
        const updatedTextElements = updatedScribe.elements.filter(element => element.type === 'Text');
        yield put(updateBitmapFonts(updatedTextElements));
      }
    } else {
      yield put(upgradeAccount(UpgradeSubscriptionClickedEventTrigger.ELEMENT_LIMIT));
    }
  }
}

function* pasteText(scribe: ExistingScribeModel, text: string) {
  yield put(createTextElementAction(scribe.id, text));
}
